import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  decideBidding,
  decideInquiry,
  type BiddingDecision,
  type InquiryDecision,
} from "@shared/fees";
import { api, ApiRequestError, type Actor, type BootstrapData } from "./api";
import type { TranslationKey } from "@/i18n";

/**
 * 登入狀態
 * ═══════════════════════════════════════════════════════════════
 *
 * session token 是 HttpOnly cookie，JavaScript 讀不到（XSS 也偷不走）。
 * 所以前端無法自己判斷「有沒有登入」—— 必須問一次後端。
 *
 * 啟動時打一次 /api/bootstrap：它同時回傳身分與市場設定，
 * 一個往返就把 app 需要的初始狀態拿齊，不用打兩支 API。
 *
 * 狀態有三種，不是兩種：
 *   loading  還不知道（第一次 bootstrap 還沒回來）
 *   actor    已登入
 *   null     確定未登入
 *
 * 少了 loading 這一態，畫面會先閃一下「登入 / 註冊」按鈕才換成使用者名稱。
 */

interface AuthValue {
  actor: Actor | null;
  bootstrap: BootstrapData | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (input: SignUpInput) => Promise<void>;
  signOut: () => Promise<void>;
  /** 重新抓 bootstrap。改了頭像或名稱之後要呼叫，導覽列才會跟著換。 */
  refresh: () => Promise<void>;

  /**
   * 能不能投標，以及不能的原因。
   *
   * 這是全站唯一判斷「可不可以送提案」的地方。
   * 任何顯示投標入口的畫面都要先問它 ——
   * 把表單畫出來、按下去才說不行，是最糟的做法：
   * 使用者花了十分鐘寫提案，最後才發現自己根本不能投。
   */
  bidding: BiddingState;

  /**
   * 能不能主動聯絡對方，以及不能的原因。
   *
   * as 是「我用哪一邊的身分聯絡」：在人才頁上是 client，
   * 在案件頁上聯絡發案方是 freelancer。
   */
  inquiry: (as: "client" | "freelancer") => InquiryDecision;
}

/**
 * 前端的投標資格。
 *
 * 除了 guest／client 這兩個「不屬於會員狀態」的情況之外，
 * 判斷本身直接用 shared 的 decideBidding —— 跟 contracts 服務
 * 是同一份程式碼，所以不會出現「畫面說可以、送出說不行」。
 */
export type BiddingState = BiddingDecision | { can: false; reason: "guest" | "client" };

export interface SignUpInput {
  email: string;
  password: string;
  displayName: string;
  role: "client" | "freelancer" | "both";
  countryCode: string;
  timezone: string;
  /**
   * 使用者當下在讀的語言。
   *
   * 只有寄信會用到 —— 伺服器看不到網址，所以「這個人讀哪一種
   * 語言」必須在註冊的這一刻帶上去，之後就沒有機會了。
   */
  locale: string;

  /*
    條款同意。送版本字串而不是布林值 —— 要記錄的是「同意了哪一版」，
    而伺服器會比對現行版本，舊分頁送出的舊版本會被拒絕。
    reviewAcknowledged 對應消保法 §11-1 的審閱期間確認。
  */
  termsVersion: string;
  privacyVersion: string;
  cookiesVersion: string;
  reviewAcknowledged: true;
  /* 蜜罐。真人永遠送空字串 —— 見 shared/signup-guard.ts */
  website?: string;
  /* Cloudflare Turnstile 的 token；沒有 sitekey 的環境不送 */
  turnstileToken?: string;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setBootstrap(await api.bootstrap());
    } catch {
      // bootstrap 失敗（例如 gateway 暫時掛掉）不該讓整個 app 崩掉。
      // 保持未登入狀態，使用者仍能瀏覽公開內容。
      setBootstrap(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthValue>(
    () => ({
      actor: bootstrap?.actor ?? null,
      bootstrap,
      loading,
      async signIn(email, password) {
        await api.login({ email, password });
        // 重新 bootstrap 而不是直接用登入回應：
        // 市場可能因為登入而改變（訪客看 IP，會員看帳號綁定的市場）
        await refresh();
      },
      async signUp(input) {
        await api.signup(input as unknown as Record<string, unknown>);
        await refresh();
      },
      async signOut() {
        await api.logout().catch(() => {});
        await refresh();
      },
      refresh,
      bidding: resolveBidding(bootstrap),
      inquiry: (as) =>
        decideInquiry({
          role: (bootstrap?.actor?.role as "client" | "freelancer" | "both") ?? null,
          as,
          /*
           * 直接用 session 帶回來的值。
           *
           * 不能退回 membership.emailVerified —— 發案方沒有接案者檔案，
           * membership 是 null，那樣寫會讓未驗證的發案方矇混過關。
           */
          emailVerified: bootstrap?.actor?.emailVerified ?? false,
          /*
           * 手機驗證狀態要從 actor 拿，不是從 membership 拿 ——
           * 發案方沒有接案者檔案，membership 是 null，
           * 但他一樣要通過手機驗證才能主動聯絡別人。
           */
          phoneVerified: bootstrap?.actor?.phoneVerified,
          idVerified: bootstrap?.actor?.idVerified,
          membership: bootstrap?.membership
            ? {
                startedAt: bootstrap.membership.startedAt,
                paidUntil: bootstrap.membership.paidUntil ?? undefined,
                emailVerified: bootstrap.membership.emailVerified,
              }
            : null,
        }),
    }),
    [bootstrap, loading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * 把 bootstrap 的資料翻成一個「能不能投標」的答案。
 *
 * 判斷邏輯本身在 shared/fees.ts（membershipState），
 * 這裡只負責把「未登入」「發案方」這兩種不屬於會員狀態的情況接上去。
 */
function resolveBidding(bootstrap: BootstrapData | null): BiddingState {
  const actor = bootstrap?.actor;
  if (!actor) return { can: false, reason: "guest" };
  if (actor.role === "client") return { can: false, reason: "client" };

  const raw = bootstrap?.membership;
  return decideBidding(
    raw
      ? {
          startedAt: raw.startedAt,
          paidUntil: raw.paidUntil ?? undefined,
          emailVerified: raw.emailVerified,
          /*
            這一欄以前沒帶，而那是一個會讓人白寫十分鐘提案的 bug。

            identity 回的是 Boolean(phoneVerifiedAt) —— 永遠是真的布林值，
            沒驗過就是 false。前端不帶這一欄時 decideBidding 收到 undefined
            會放行（那是給舊資料的寬容），於是畫面畫出投標表單、
            伺服器卻回 phone_unverified。

            fees.ts 開頭寫著「兩邊用同一個函式，才不會出現畫面說可以、
            送出說不行」—— 同一個函式不夠，餵給它的資料也要一樣。
          */
          phoneVerified: actor.phoneVerified,
          idVerified: actor.idVerified,
            /*
              和 phoneVerified 同一個道理：不帶的話 decideBidding
              收到 undefined 當成 0，畫面會說「還可以投」，
              而伺服器那邊的計數器早就滿了。
            */
            preIdUsed: raw.preIdUsed,
        }
      : null,
  );
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/**
 * 把 API 錯誤轉成使用者看得懂的中文。
 *
 * 後端的訊息是給開發者看的英文，直接顯示給使用者是偷懶。
 * 有欄位級錯誤時優先顯示那個 —— 「密碼至少 10 個字元」
 * 比「Request validation failed」有用得多。
 */
/** 後端錯誤碼 → 字典的 key */
const ERROR_KEYS = {
  unexpected: "err.unexpected",
  badCredentials: "err.badCredentials",
  emailTaken: "err.emailTaken",
  tooMany: "err.tooMany",
  unavailable: "err.unavailable",
  emailFirst: "err.emailFirst",
  phoneFirst: "err.phoneFirst",
  idPending: "err.idPending",
  pendingApproval: "err.pendingApproval",
  smsUnavailable: "err.smsUnavailable",
} as const satisfies Record<string, TranslationKey>;

export function readableError(
  error: unknown,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
): string {
  /*
    t 是參數而不是在函式裡呼叫 useI18n()。

    這個函式不是元件也不是 hook —— 它會在 catch 區塊裡被呼叫，
    那不是 React 的 render 階段，hook 在那裡是不合法的。
    把 t 當參數傳進來，型別也順便保證了呼叫端一定拿得到翻譯。
  */
  if (!(error instanceof ApiRequestError)) return t(ERROR_KEYS.unexpected);

  /*
    伺服器給了 key 就翻它。

    在這之前 bad_request 與 conflict 是把 error.message 原樣顯示 ——
    而那些訊息在服務端是寫死的中文。於是法國的使用者對自己發布的
    案件投標，看到的是「不能對自己發布的案件投標」。

    key 排在欄位錯誤之前：欄位錯誤來自 zod，是英文的技術訊息
    （"String must contain at least 8 character(s)"），而帶 key 的
    那一句是我們特地為使用者寫的。
  */
  /*
    params 是伺服器端給的插值（上限數字、剩餘分鐘數）。

    那些數字只有伺服器知道 —— 前端不該把 PORTFOLIO_MAX_ITEMS
    再抄一份，抄一份就會有一天兩邊對不上，而畫面上顯示的
    是錯的那一個。
  */
  if (error.key) return t(error.key as TranslationKey, error.params);

  const firstField = error.fields && Object.values(error.fields)[0]?.[0];
  if (firstField) return firstField;

  switch (error.code) {
    case "unauthorized":
      return t(ERROR_KEYS.badCredentials);
    case "conflict":
      return error.message.includes("Email") ? t(ERROR_KEYS.emailTaken) : error.message;
    case "bad_request":
      return error.message;
    case "rate_limited":
      return t(ERROR_KEYS.tooMany);
    case "upstream_unavailable":
      return t(ERROR_KEYS.unavailable);
    /*
      這三個都是「你差一步」而不是「壞掉了」。

      落到 default 的話使用者只會看到「發生未預期的錯誤」——
      那是最糟的一種訊息：它同時沒告訴他發生什麼事、
      也沒告訴他該做什麼，而這正是他要完成一個動作的當下。
    */
    case "email_first":
      return t(ERROR_KEYS.emailFirst);
    case "phone_unverified":
      return t(ERROR_KEYS.phoneFirst);
    /*
      證件那一關的訊息要說「在等人」而不是「你還沒做」——
      使用者可能已經上傳完在等審核，叫他「去驗證」他只會再傳一次。
    */
    case "id_unverified":
      return t(ERROR_KEYS.idPending);
    /*
      ── 核准和證件是兩件不同的事 ──────────────────────────────

      證件那一關他還有事可以做（去上傳）。核准這一關他**什麼都
      不必做、也不能做**，只能等。

      兩者混成同一句話的代價是：一個已經送出證件、正在等核准的人
      會以為自己漏了什麼，然後再送一次證件 —— 而那會在後台產生
      一筆重複的審核，並且讓他更確信「這個站壞了」。
    */
    case "pending_approval":
      return t(ERROR_KEYS.pendingApproval);
    case "sms_unavailable":
      return t(ERROR_KEYS.smsUnavailable);
    default:
      return t(ERROR_KEYS.unexpected);
  }
}
